// Sends a file's bytes with XMLHttpRequest: fetch has no upload progress. Shared by the files
// POC (a presigned PUT) and the knowledge uploads of the cosmos API (a raw POST).

export interface SendRequest {
  method: string;
  url: string;
  headers: Record<string, string>;
}

/** The answer's status and text; 0 when the server could not be reached. */
export interface Sent {
  status: number;
  text: string;
}

/** Sends `body`; resolves with any answer, rejects with an AbortError when cancelled. */
export function sendFile(
  request: SendRequest,
  body: Blob,
  { onProgress, signal }: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
): Promise<Sent> {
  return new Promise((resolve, reject) => {
    const aborted = () => new DOMException("The upload was cancelled.", "AbortError");
    if (signal?.aborted) {
      reject(aborted());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open(request.method, request.url);
    for (const [name, value] of Object.entries(request.headers)) xhr.setRequestHeader(name, value);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText ?? "" });
    xhr.onerror = () => resolve({ status: 0, text: "" });
    xhr.onabort = () => reject(aborted());
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}
